/**
 * @fileoverview Currency formatting utilities using Intl.NumberFormat.
 * @module shared/utils/money
 */

/**
 * Determines the most appropriate locale to use for a given currency.
 * Ensures currencies with postfixed symbols (like PLN) render correctly even when
 * the UI language differs from the locale that matches currency conventions.
 */
function resolveCurrencyLocale(
  currency: string,
  locale: string | undefined,
  display: 'symbol' | 'narrowSymbol' | 'code'
): string | undefined {
  if (display === 'code') {
    return locale ?? undefined
  }

  if (currency === 'USD') {
    return locale ?? 'en-US'
  }

  if (currency === 'PLN') {
    // Use Polish locale conventions for PLN to ensure comma decimals and proper placement
    return locale ?? 'pl-PL'
  }

  return locale ?? undefined
}

const currencySymbolCache = new Map<string, string>()

function getCurrencySymbol(currency: string): string {
  const cached = currencySymbolCache.get(currency)
  if (cached) return cached

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  })
  const symbol =
    formatter.formatToParts(0).find(part => part.type === 'currency')?.value ?? currency
  currencySymbolCache.set(currency, symbol)
  return symbol
}

function normalizeCurrencyOutput(formatted: string, currency: string): string {
  if (currency !== 'PLN') {
    return formatted
  }

  const symbol = getCurrencySymbol(currency)
  const cleaned = formatted.replace(/\u00a0/g, ' ').trim()
  if (cleaned.endsWith(symbol)) {
    return cleaned
  }

  const match = cleaned.match(/^([+\-]?)(.*)$/)
  const sign = match?.[1] ?? ''
  const remainder = (match?.[2] ?? '').trim()

  if (remainder.startsWith(symbol)) {
    const withoutSymbol = remainder.slice(symbol.length).trim()
    return `${`${sign}${withoutSymbol}`.trim()} ${symbol}`.trim()
  }

  if (remainder.includes(symbol)) {
    return cleaned
  }

  return `${`${sign}${remainder}`.trim()} ${symbol}`.trim()
}

/**
 * Format a monetary value using Intl.NumberFormat with the given currency.
 *
 * - Defaults to 2 fraction digits.
 * - Locale is optional and falls back to the runtime default when omitted.
 * - Currency display defaults to 'symbol'; pass 'narrowSymbol' to prefer compact signs like '$'.
 *
 * @param value Numeric amount in major currency units (e.g., dollars)
 * @param currency ISO-4217 currency code (e.g., 'USD', 'EUR', 'PLN')
 * @param locale Optional BCP 47 locale (e.g., 'en-US', 'pl-PL'). If omitted, uses runtime default.
 * @param minFraction Optional minimum fraction digits (default 2)
 * @param maxFraction Optional maximum fraction digits (default 2)
 * @param display Currency display style ('symbol' | 'narrowSymbol' | 'code'). Default 'symbol'.
 * @returns Formatted currency string (e.g., "$1,234.50")
 * @example
 * ```ts
 * formatMoney(1234.5, 'USD', 'en-US') // => "$1,234.50"
 * formatMoney(1234, 'PLN', 'pl-PL')   // => "1 234,00 zł"
 * ```
 */
export function formatMoney(
  value: number,
  currency: string,
  locale?: string,
  minFraction = 2,
  maxFraction = 2,
  display: 'symbol' | 'narrowSymbol' | 'code' = 'symbol',
  useGrouping = true
): string {
  const effectiveLocale = resolveCurrencyLocale(currency, locale, display)
  const formatted = new Intl.NumberFormat(effectiveLocale, {
    style: 'currency',
    currency,
    currencyDisplay: display,
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
    useGrouping,
  }).format(value)

  return normalizeCurrencyOutput(formatted, currency)
}

/**
 * Compact currency formatting (e.g., 12.3K, 4.5M) while preserving the currency symbol.
 *
 * @param value Numeric amount
 * @param currency ISO-4217 currency code
 * @param locale Optional locale
 * @param maximumFractionDigits Optional max fraction digits (default 1)
 * @param display Currency display style (default 'symbol')
 * @returns Formatted compact currency string
 * @example
 * ```ts
 * formatMoneyCompact(12345, 'USD', 'en-US') // => "$12.3K"
 * ```
 */
export function formatMoneyCompact(
  value: number,
  currency: string,
  locale?: string,
  maximumFractionDigits = 1,
  display: 'symbol' | 'narrowSymbol' | 'code' = 'symbol'
): string {
  // For compact notation we want consistent K/M/B units regardless of UI language.
  // Using 'en-US' guarantees "77.5K" style instead of locale-specific words like
  // Polish "tys." while keeping currency symbol handling intact.
  // If the caller explicitly requests currency 'code' display, respect provided locale.
  const effectiveLocale = display === 'code' ? locale : 'en-US'
  const formatted = new Intl.NumberFormat(effectiveLocale, {
    style: 'currency',
    currency,
    currencyDisplay: display,
    notation: 'compact',
    // Force a fixed decimal precision for predictable snapshots (e.g., 50.0K)
    minimumFractionDigits: maximumFractionDigits,
    maximumFractionDigits,
  }).format(value)

  return normalizeCurrencyOutput(formatted, currency)
}

/**
 * Shared compact money formatter used by dashboard components.
 * Wraps {@link formatMoneyCompact} with the project-wide defaults:
 * - compact K/M/B notation
 * - narrow currency symbol
 *
 * @param value Numeric amount in major currency units.
 * @param currency ISO-4217 currency code (e.g., 'USD', 'EUR', 'PLN').
 * @param locale BCP 47 locale string (e.g., 'en-US', 'pl-PL').
 * @param maximumFractionDigits Optional maximum fraction digits (default 1).
 * @returns Formatted compact currency string, e.g. "254.0K zł".
 */
export function compactMoney(
  value: number,
  currency: string,
  locale: string,
  maximumFractionDigits = 1
): string {
  return formatMoneyCompact(value, currency, locale, maximumFractionDigits, 'narrowSymbol')
}
